import * as Yup from 'yup';

import { Business, User } from '@barboxx/models';
import {
    MINIMUM_TEXT,
    PHONE_REGEX,
    Roles,
    ROLES,
    TINY_TEXT,
    USER_TYPES,
    UserTypes,
} from '@barboxx/types';
import { enumToList, transformEmptyToNull } from '@barboxx/utils';

export interface CreateUser extends User.CreateRequest {
    businessIds?: number[];
}

export const USER_SCHEMA: Yup.ObjectSchema<User.UpdateRequest> = Yup.object({
    id: Yup.number().required(),
    firstName: Yup.string()
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(TINY_TEXT, 'Too Long!')
        .required('Required'),
    lastName: Yup.string()
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(TINY_TEXT, 'Too Long!')
        .required('Required'),
    email: Yup.string().email('Must be valid email').required('Required'),
    userType: Yup.string()
        .oneOf(enumToList(USER_TYPES), 'Must be user type')
        .required('Required'),
    active: Yup.boolean().default(true),
    roles: Yup.array()
        .of<Roles>(
            Yup.string().oneOf(enumToList(ROLES), 'Must be role').required()
        )
        .min(1, 'Must include at least 1 role')
        .required('Required'),
    avatar: Yup.string()
        .nullable()
        .default(null)
        .transform(transformEmptyToNull),

    phoneNumber: Yup.string()
        .matches(PHONE_REGEX, {
            message: 'Must be a phone number',
            excludeEmptyString: true,
        })
        .required()
        .nullable()
        .transform(transformEmptyToNull),
    businesses: Yup.array()
        .of<Pick<Business, 'id'>>(
            Yup.object().shape({
                id: Yup.number().required(),
            })
        )
        .transform((value: Business.DetailsResponse[]) =>
            value.map(({ id }) => ({ id }))
        )
        .optional()
        .when('userType', {
            is: (userType: UserTypes) => userType === 'Business',
            then: schema =>
                schema
                    .min(1, 'Must include at least 1 business')
                    .required('Required'),
        }),
});

export const CREATE_USER_SCHEMA: Yup.ObjectSchema<CreateUser> =
    USER_SCHEMA.omit(['id']).shape({
        businessIds: Yup.array().of<number>(Yup.number().required()).optional(),
        password: Yup.string()
            .min(8, 'Too Short!')
            .max(TINY_TEXT, 'Too Long!')
            .required(),
    });
